/**
 * Lazily-loaded page components for both route tables.
 *
 * Every page is code-split at its route boundary, so the entry bundle carries
 * only the app shell (nav, layouts, chat dock) and a feature's JavaScript —
 * along with whatever heavy libraries it alone pulls in, such as recharts for
 * Financial or CodeMirror for the editor surfaces — is fetched the first time
 * the user opens one of its routes.
 *
 * Declared once here rather than in each table so `AppRouter` and the side
 * pane's `APP_ROUTES` share a single `lazy()` wrapper per page, and therefore
 * a single chunk and a single module instance. Duplicating the wrappers would
 * load the same code under two component identities.
 *
 * Deliberately NOT here: `LandingPage` and `Login` (the first paint for a
 * signed-out visitor — splitting them only adds a round trip before anything
 * renders), the redirect stubs, and the hub/guard shells that the constrained
 * layout already imports eagerly.
 */
import { lazy } from 'react';
import type { ComponentType } from 'react';

// Barrel modules whose exports feed several routes. Declared as a shared loader
// so every page below resolves to the one chunk rather than one chunk per page.
const inboxModule = () => import('@features/inbox/pages/InboxPage');
const mealsModule = () => import('@features/meals');

// ---------------------------------------------------------------------------
// Public / marketing
// ---------------------------------------------------------------------------

export const Waitlist = lazy(() => import('@app/pages/Waitlist'));
export const PrivacyPolicy = lazy(() => import('@app/pages/PrivacyPolicy'));
export const TermsOfService = lazy(() => import('@app/pages/TermsOfService'));

// ---------------------------------------------------------------------------
// Attention / email
// ---------------------------------------------------------------------------

/**
 * Lazy-load a page that dispatches row actions, wrapped in the inbox item-action
 * gateway (see `attentionPages`). The wrapper module loads alongside the page, so
 * neither it nor the inbox action modules it carries reach the entry chunk.
 */
function attentionPage<P extends object>(loadPage: () => Promise<ComponentType<P>>) {
  return lazy(() =>
    Promise.all([loadPage(), import('@app/routes/attentionPages')]).then(
      ([Page, { withInboxItemActions }]) => ({ default: withInboxItemActions(Page) })
    )
  );
}

export const EmailPage = attentionPage(() =>
  import('@features/email/pages/EmailPage').then(m => m.default)
);
export const EmailLabelsPage = attentionPage(() =>
  import('@features/email/pages/EmailLabels').then(m => m.EmailLabelsPage)
);
export const EmailSettingsPage = lazy(() => import('@features/email/pages/EmailSettingsPage'));
export const SpamPage = attentionPage(() =>
  import('@features/spam/pages/SpamPage').then(m => m.SpamPage)
);
export const InboxPage = attentionPage(() => inboxModule().then(m => m.default));
export const HistoryInboxPage = attentionPage(() => inboxModule().then(m => m.HistoryInboxPage));
export const LaterInboxPage = attentionPage(() => inboxModule().then(m => m.LaterInboxPage));
export const BulletinBoardInboxPage = attentionPage(() =>
  inboxModule().then(m => m.BulletinBoardInboxPage)
);
export const SnoozedInboxPage = attentionPage(() => inboxModule().then(m => m.SnoozedInboxPage));

// ---------------------------------------------------------------------------
// Chat / conversations / communications
// ---------------------------------------------------------------------------

export const Chat = lazy(() => import('@features/chat/pages/Chat'));
export const Conversations = lazy(() => import('@features/conversations/pages/Conversations'));
export const MessagesHubPage = lazy(() => import('@features/communications/pages/MessagesHubPage'));

// ---------------------------------------------------------------------------
// Financial — the heaviest feature, and the only consumer of recharts
// ---------------------------------------------------------------------------

export const OverviewPage = lazy(() => import('@features/financial/pages/Overview'));
export const TransactionsPage = lazy(() => import('@features/financial/pages/Transactions'));
export const InvestmentsPage = lazy(() => import('@features/financial/pages/Investments'));
export const BudgetPage = lazy(() => import('@features/financial/pages/Budget'));
export const BudgetCategoryPage = lazy(() => import('@features/financial/pages/BudgetCategory'));
export const CashFlowPage = lazy(() => import('@features/financial/pages/CashFlow'));
export const RetirementPage = lazy(() => import('@features/financial/pages/Retirement'));
export const DebtSavingsPage = lazy(() => import('@features/financial/pages/DebtSavings'));
export const JobsTab = lazy(() => import('@features/financial/pages/Financial/tabs/JobsTab'));
export const ReportsPage = lazy(() => import('@features/financial/pages/Reports'));
export const ReportView = lazy(() => import('@features/financial/pages/ReportView'));
export const SpendingAnalysisPage = lazy(() =>
  import('@features/financial/pages/SpendingAnalysis').then(m => ({
    default: m.SpendingAnalysisPage,
  }))
);
export const SpendingByTagPage = lazy(() =>
  import('@features/financial/pages/SpendingByTag').then(m => ({ default: m.SpendingByTagPage }))
);

// ---------------------------------------------------------------------------
// Meals / food diary
// ---------------------------------------------------------------------------

export const MealsLayout = lazy(() => mealsModule().then(m => ({ default: m.MealsLayout })));
export const FoodDiaryLayout = lazy(() =>
  mealsModule().then(m => ({ default: m.FoodDiaryLayout }))
);
export const FoodDiaryFoodsPage = lazy(() =>
  mealsModule().then(m => ({ default: m.FoodDiaryFoodsPage }))
);
export const ThisWeekPage = lazy(() => mealsModule().then(m => ({ default: m.ThisWeekPage })));
export const FoodsPage = lazy(() => mealsModule().then(m => ({ default: m.FoodsPage })));
export const FoodEditorPage = lazy(() => mealsModule().then(m => ({ default: m.FoodEditorPage })));
export const MealsSettingsPage = lazy(() => mealsModule().then(m => ({ default: m.SettingsPage })));
export const BlockedDaysPage = lazy(() =>
  mealsModule().then(m => ({ default: m.BlockedDaysPage }))
);
export const NutritionPage = lazy(() => mealsModule().then(m => ({ default: m.NutritionPage })));
export const TripMenusPage = lazy(() => mealsModule().then(m => ({ default: m.TripMenusPage })));
export const TripMenuDetailPage = lazy(() =>
  mealsModule().then(m => ({ default: m.TripMenuDetailPage }))
);

// ---------------------------------------------------------------------------
// Editor surfaces — these carry CodeMirror and its language packs
// ---------------------------------------------------------------------------

export const SourceEditorPage = lazy(() => import('@features/notes/SourceEditorPage'));
export const StudyPage = lazy(() => import('@features/study/pages/StudyPage'));
export const IdeaFactoryPage = lazy(() => import('@features/study/pages/IdeaFactoryPage'));
export const WritingBoardPage = lazy(
  () => import('@features/writing-board/pages/WritingBoardPage')
);
export const JournalPage = lazy(() => import('@features/journal/pages/JournalPage'));

// ---------------------------------------------------------------------------
// My data / knowledge
// ---------------------------------------------------------------------------

export const ContactsPage = lazy(() =>
  import('@features/contacts').then(m => ({ default: m.ContactsPage }))
);
export const Highlights = lazy(() => import('@features/highlights'));
export const MemoriesPage = lazy(() => import('@features/memories/pages/MemoriesPage'));
export const AgendaPage = lazy(() => import('@features/agenda').then(m => ({ default: m.Agenda })));
export const Observers = lazy(() => import('@features/observers/pages/Observers'));
export const WellnessDashboard = lazy(() => import('@features/wellness'));
export const Ambient = lazy(() => import('@features/ambient').then(m => ({ default: m.Ambient })));
export const HouseholdPage = lazy(() =>
  import('@features/household').then(m => ({ default: m.HouseholdPage }))
);
export const ResearchPage = lazy(() => import('@features/research/pages/ResearchPage'));
export const SourceReaderPage = lazy(() => import('@features/research/pages/SourceReaderPage'));

// ---------------------------------------------------------------------------
// Everything else
// ---------------------------------------------------------------------------

export const AllAreasPage = lazy(() => import('@features/hubs/AllAreasPage'));
export const HomePage = lazy(() => import('@features/home/pages/HomePage'));
export const SearchPage = lazy(() => import('@shared/search/SearchPage'));
export const TasksPage = lazy(() => import('@features/tasks/pages/TasksPage'));
export const TeleprompterPage = lazy(() => import('@features/teleprompter/pages/TeleprompterPage'));
export const SettingsPage = lazy(() => import('@features/settings/SettingsPage'));
export const Quotes = lazy(() => import('@features/quotes'));
export const CostPage = lazy(() => import('@features/cost/pages/CostPage'));
export const PromptLibraryPage = lazy(() =>
  import('@features/prompt-library').then(m => ({ default: m.PromptLibraryPage }))
);
export const WorkflowEditorPage = lazy(() =>
  import('@features/prompt-library/components/WorkflowEditorPage').then(m => ({
    default: m.WorkflowEditorPage,
  }))
);

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export const LLMProvidersTab = lazy(() => import('@features/system/components/LLMProvidersTab'));
export const MCPServersTab = lazy(() => import('@features/system/components/MCPServersTab'));
export const CapabilitiesTab = lazy(() => import('@features/system/components/CapabilitiesTab'));
export const SettingsTab = lazy(() => import('@features/system/components/SettingsTab'));
export const AgentLogsTab = lazy(() => import('@features/system/components/AgentLogsTab'));
export const ServiceHealthTab = lazy(() => import('@features/system/components/ServiceHealthTab'));
